import { useCallback, useEffect, useRef, useState } from 'react';
import './SnakeGame.css';

const COLS = 20;
const ROWS = 15;
const TICK_MS = 150;

const DIRECTIONS = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  w: { x: 0, y: -1 },
  s: { x: 0, y: 1 },
  a: { x: -1, y: 0 },
  d: { x: 1, y: 0 },
  W: { x: 0, y: -1 },
  S: { x: 0, y: 1 },
  A: { x: -1, y: 0 },
  D: { x: 1, y: 0 },
};

const GAME_KEYS = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'w',
  'a',
  's',
  'd',
  'W',
  'A',
  'S',
  'D',
  ' ',
  'p',
  'P',
  'r',
  'R',
  'Escape',
  'q',
  'Q',
];

const INITIAL_SNAKE = [
  { x: 5, y: 7 },
  { x: 4, y: 7 },
  { x: 3, y: 7 },
];

const GLYPHS = {
  head: '@',
  body: 'o',
  food: '*',
  empty: '\u00A0',
};

function initialFood() {
  // Deterministic starting food placed away from the initial snake.
  return { x: 12, y: 7 };
}

function randomFood(snake) {
  let pos;
  do {
    pos = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) };
  } while (snake.some((seg) => seg.x === pos.x && seg.y === pos.y));
  return pos;
}

function initialState() {
  return {
    snake: INITIAL_SNAKE,
    food: initialFood(),
    score: 0,
    paused: false,
    gameOver: false,
  };
}

export default function SnakeGame({ onExit }) {
  const [game, setGame] = useState(() => initialState());
  const gameRef = useRef(game);
  const directionRef = useRef({ x: 1, y: 0 });
  const nextDirectionRef = useRef({ x: 1, y: 0 });
  const [showTouch, setShowTouch] = useState(false);

  const reset = useCallback(() => {
    const fresh = initialState();
    gameRef.current = fresh;
    directionRef.current = { x: 1, y: 0 };
    nextDirectionRef.current = { x: 1, y: 0 };
    setGame(fresh);
  }, []);

  const quit = useCallback(() => {
    onExit();
  }, [onExit]);

  const applyInput = useCallback(
    (key) => {
      const s = gameRef.current;
      if (key === 'Escape' || key === 'q' || key === 'Q') {
        quit();
        return;
      }

      if (s.gameOver) {
        if (key === 'r' || key === 'R') reset();
        return;
      }

      if (key === ' ' || key === 'p' || key === 'P') {
        const updated = { ...s, paused: !s.paused };
        gameRef.current = updated;
        setGame(updated);
        return;
      }

      if (key === 'r' || key === 'R') {
        reset();
        return;
      }

      const dir = DIRECTIONS[key];
      if (!dir) return;

      const currentDir = directionRef.current;
      if (dir.x !== -currentDir.x || dir.y !== -currentDir.y) {
        nextDirectionRef.current = dir;
      }
    },
    [quit, reset]
  );

  const doTick = useCallback(() => {
    const s = gameRef.current;
    if (s.paused || s.gameOver) return;

    const currentDir = directionRef.current;
    let nextDir = nextDirectionRef.current;
    if (nextDir.x === -currentDir.x && nextDir.y === -currentDir.y) {
      nextDir = currentDir;
    }

    const head = s.snake[0];
    const newHead = {
      x: (head.x + nextDir.x + COLS) % COLS,
      y: (head.y + nextDir.y + ROWS) % ROWS,
    };

    if (s.snake.some((seg) => seg.x === newHead.x && seg.y === newHead.y)) {
      const updated = { ...s, gameOver: true };
      gameRef.current = updated;
      setGame(updated);
      return;
    }

    const ate = newHead.x === s.food.x && newHead.y === s.food.y;
    const newSnake = [newHead, ...s.snake];
    let newFood = s.food;
    let newScore = s.score;

    if (ate) {
      newScore += 10;
      newFood = randomFood(newSnake);
    } else {
      newSnake.pop();
    }

    const updated = { ...s, snake: newSnake, food: newFood, score: newScore };
    gameRef.current = updated;
    directionRef.current = nextDir;
    setGame(updated);
  }, []);

  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  useEffect(() => {
    const id = setInterval(doTick, TICK_MS);
    return () => clearInterval(id);
  }, [doTick]);

  useEffect(() => {
    const handleKey = (e) => {
      if (GAME_KEYS.includes(e.key)) {
        e.preventDefault();
      }
      applyInput(e.key);
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [applyInput]);

  useEffect(() => {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const update = () => setShowTouch(isTouch && window.innerWidth <= 640);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handleDPad = (key) => {
    applyInput(key);
  };

  const { snake, food, score, paused, gameOver } = game;

  const cells = [];
  for (let y = 0; y < ROWS; y += 1) {
    for (let x = 0; x < COLS; x += 1) {
      const isHead = snake[0].x === x && snake[0].y === y;
      const isBody = !isHead && snake.some((seg) => seg.x === x && seg.y === y);
      const isFood = food.x === x && food.y === y;
      let glyph = GLYPHS.empty;
      let className = 'snake-cell';
      let testId = undefined;
      if (isHead) {
        glyph = GLYPHS.head;
        className += ' snake-head';
        testId = 'snake-head';
      } else if (isBody) {
        glyph = GLYPHS.body;
        className += ' snake-body';
        testId = 'snake-body';
      } else if (isFood) {
        glyph = GLYPHS.food;
        className += ' snake-food';
        testId = 'snake-food';
      }
      cells.push(
        <div
          key={`${x}-${y}`}
          className={className}
          data-testid={testId}
        >
          <span className="snake-glyph" aria-hidden="true">{glyph}</span>
        </div>
      );
    }
  }

  return (
    <div className="snake-game" role="application" aria-label="ASCII Snake game">
      <div className="snake-hud">
        <span className="snake-title">SNAKE</span>
        <span className="snake-score" data-testid="snake-score">
          Score: {score}
        </span>
      </div>
      <div className="snake-board-shell">
        <div className="snake-board" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
          {cells}
        </div>
        {paused && !gameOver && (
          <div className="snake-overlay" role="status" aria-live="polite">
            <div className="snake-overlay-box">
              <p>PAUSED</p>
              <p className="snake-overlay-hint">Space/P to resume</p>
            </div>
          </div>
        )}
        {gameOver && (
          <div className="snake-overlay" role="alert">
            <div className="snake-overlay-box">
              <p>GAME OVER</p>
              <p className="snake-score-final">Final score: {score}</p>
              <div className="snake-actions">
                <button type="button" onClick={reset}>Restart</button>
                <button type="button" onClick={quit}>Quit</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="snake-controls">
        arrows/wasd move · space/p pause · r restart · esc/q quit
      </div>
      {showTouch && (
        <div className="snake-dpad">
          <button type="button" className="dpad-btn" aria-label="Up" onClick={() => handleDPad('ArrowUp')}>▲</button>
          <div className="dpad-row">
            <button type="button" className="dpad-btn" aria-label="Left" onClick={() => handleDPad('ArrowLeft')}>◀</button>
            <button type="button" className="dpad-btn" aria-label="Down" onClick={() => handleDPad('ArrowDown')}>▼</button>
            <button type="button" className="dpad-btn" aria-label="Right" onClick={() => handleDPad('ArrowRight')}>▶</button>
          </div>
        </div>
      )}
    </div>
  );
}
